import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import IosShareIcon from "@mui/icons-material/IosShare";
import CheckIcon from "@mui/icons-material/Check";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CloseIcon from "@mui/icons-material/Close";
import {
  DEMO_SESSION_CODE_DEFAULT,
  getDemoJoinPublicUrl,
} from "../utils/demoClub";
import "./DemoPhoneJoinLink.css";

const JOIN_DEMO_QR_SRC = `${process.env.PUBLIC_URL || ""}/JoinDemo.png`;

export function DemoPhoneJoinLink({
  className = "",
  compact = false,
  showActions = true,
  fullscreen = false,
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const url = useMemo(() => getDemoJoinPublicUrl(), []);

  const copy = useCallback(async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      try {
        window.prompt(t("demoPhoneLinkCopy", "Copy this link"), url);
      } catch {
        /* ignore */
      }
    }
  }, [url, t]);

  const share = useCallback(async () => {
    if (!url) return;
    if (typeof navigator.share === "function") {
      try {
        await navigator.share({
          title: t("demoJoinTitle", "Join demo training"),
          text: t(
            "demoPhoneLinkShareText",
            "Scan the QR code or open this link on your phone, enter session code {{code}}, then your nickname to join live training.",
            { code: DEMO_SESSION_CODE_DEFAULT }
          ),
          url,
        });
        return;
      } catch {
        /* user cancelled or share failed — fall through to copy */
      }
    }
    copy();
  }, [url, t, copy]);

  if (!url) return null;

  return (
    <div
      className={[
        "demo-phone-link",
        compact ? "demo-phone-link--compact" : "",
        fullscreen ? "demo-phone-link--fullscreen" : "",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {!compact || fullscreen ? (
        <p className="demo-phone-link__hint">
          {t(
            "demoPhoneLinkQrHint",
            "Scan this QR code on a phone. Players enter session code {{code}} (default), a nickname, and recording consent to join live training.",
            { code: DEMO_SESSION_CODE_DEFAULT }
          )}
        </p>
      ) : null}
      <div className="demo-phone-link__qr-wrap">
        <img
          className="demo-phone-link__qr"
          src={JOIN_DEMO_QR_SRC}
          alt={t(
            "demoPhoneLinkQrAlt",
            "QR code to join live training demo on phone"
          )}
          width={fullscreen ? 480 : 220}
          height={fullscreen ? 480 : 220}
          loading={fullscreen ? "eager" : "lazy"}
          decoding="async"
        />
      </div>
      <p className="demo-phone-link__code">
        {t("demoPhoneLinkCodeLabel", "Session code")}:{" "}
        <strong>{DEMO_SESSION_CODE_DEFAULT}</strong>
      </p>
      {showActions ? (
        <div className="demo-phone-link__actions">
          <button type="button" className="demo-phone-link__btn" onClick={copy}>
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
            {copied ? t("copied", "Copied") : t("copyLink", "Copy link")}
          </button>
          <button
            type="button"
            className="demo-phone-link__btn demo-phone-link__btn--share"
            onClick={share}
          >
            <IosShareIcon fontSize="small" />
            {t("share", "Share")}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function DemoGuestInviteQrOverlay({ open, onClose }) {
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="demo-guest-qr-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={t("demoGuestInviteTitle", "Invite guest by phone")}
      onClick={() => onClose?.()}
    >
      <div
        className="demo-guest-qr-overlay__panel"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          className="demo-guest-qr-overlay__close"
          onClick={() => onClose?.()}
          aria-label={t("demoGuestQrHide", "Hide QR code")}
        >
          <CloseIcon fontSize="medium" />
        </button>
        <h2 className="demo-guest-qr-overlay__title">
          {t("demoGuestInviteTitle", "Invite guest by phone")}
        </h2>
        <DemoPhoneJoinLink fullscreen showActions />
        <button
          type="button"
          className="demo-guest-qr-overlay__hide-btn"
          onClick={() => onClose?.()}
        >
          {t("demoGuestQrHide", "Hide QR code")}
        </button>
      </div>
    </div>,
    document.body
  );
}

export function DemoGuestInviteQrToggleButton({
  open = false,
  onToggle,
  className = "",
}) {
  const { t } = useTranslation();
  if (typeof onToggle !== "function") return null;

  return (
    <button
      type="button"
      className={`demo-guest-qr-toggle ${open ? "demo-guest-qr-toggle--active" : ""} ${className}`.trim()}
      onClick={onToggle}
      aria-pressed={open}
      aria-expanded={open}
      title={
        open
          ? t("demoGuestQrHide", "Hide QR code")
          : t("demoGuestQrShow", "Show QR code")
      }
    >
      <QrCode2Icon fontSize="small" aria-hidden />
      {open
        ? t("demoGuestQrHide", "Hide QR code")
        : t("demoGuestQrShow", "Show QR code")}
    </button>
  );
}

export function DemoGuestInviteCollapsible({
  className = "",
  defaultExpanded = false,
  children = null,
  qrOnly = false,
  qrPresentation = "inline",
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [qrOpen, setQrOpen] = useState(false);
  const useFullscreenQr = qrPresentation === "fullscreen";

  return (
    <>
      <div
        className={`demo-guest-invite${expanded ? " demo-guest-invite--open" : ""} ${className}`.trim()}
      >
        <button
          type="button"
          className="demo-guest-invite__toggle"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
        >
          <span className="demo-guest-invite__toggle-label">
            {t("demoGuestInviteTitle", "Invite guest by phone")}
          </span>
          <ExpandMoreIcon
            className="demo-guest-invite__chevron"
            fontSize="small"
            aria-hidden
          />
        </button>
        {expanded ? (
          <div className="demo-guest-invite__body">
            {useFullscreenQr ? (
              <button
                type="button"
                className="demo-guest-invite__qr-open"
                onClick={() => setQrOpen(true)}
              >
                <QrCode2Icon fontSize="small" aria-hidden />
                {t("demoGuestQrShow", "Show QR code")}
              </button>
            ) : (
              <DemoPhoneJoinLink compact={qrOnly} />
            )}
            {children}
          </div>
        ) : null}
      </div>
      {useFullscreenQr ? (
        <DemoGuestInviteQrOverlay open={qrOpen} onClose={() => setQrOpen(false)} />
      ) : null}
    </>
  );
}

export default DemoPhoneJoinLink;
